import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/openplay/OpenPlayIcon';
import PicklingLogoMark from '../../../components/PicklingLogoMark';

const HeroSection = ({ onGetStarted }) => {
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasVideoPlayedOnce, setHasVideoPlayedOnce] = useState(false);

  const heroImageRef = useRef(null);
  const revealObserverRef = useRef(null);
  const featureImageRefs = useRef({});
  const liveImageRef = useRef(null);
  const linkImageRef = useRef(null);
  const [isHeroImageVisible, setIsHeroImageVisible] = useState(false);
  const [visibleFeatureImages, setVisibleFeatureImages] = useState({});

  const patternImageUrl = "url('/assets/images/picklaball-backgroundjpg.jpg')";

  const featureRows = [
    {
      title: 'EVENT',
      subtitle: '모든 플레이를 한눈에!',
      description: '오픈게임, 레슨, 트레이닝, 매칭 정보를 한 번에 모아 쉽게 이벤트를 탐색합니다.',
      icon: 'MapIcon',
      imageSrc: '/assets/images/pickleball-3.png',
      align: 'left',
    },
    {
      title: 'PICK & MAKE',
      subtitle: '찾고, 만들고!',
      description: '원하는 조건으로 Pick! 새로운 모임을 Make! 우리끼리 즐길 일정을 빠르게 시작하세요.',
      icon: 'UserGroupIcon',
      imageSrc: '/assets/images/pickleball-4.png',
      align: 'right',
    },
  ];

  const bandStyle = {
    background: 'linear-gradient(90deg, #ff7a45 0%, #ff6a3a 100%)',
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsHeroImageVisible(true);
      setVisibleFeatureImages({ EVENT: true, 'PICK & MAKE': true });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry?.isIntersecting) return;
          const key = entry.target?.dataset?.revealKey;
          if (key === 'hero') {
            setIsHeroImageVisible(true);
          } else if (key) {
            setVisibleFeatureImages((prev) => ({ ...prev, [key]: true }));
          }
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35, rootMargin: '0px 0px -10% 0px' }
    );

    revealObserverRef.current = observer;

    if (heroImageRef.current) {
      heroImageRef.current.dataset.revealKey = 'hero';
      observer.observe(heroImageRef.current);
    }

    if (liveImageRef.current) {
      liveImageRef.current.dataset.revealKey = 'band_live';
      observer.observe(liveImageRef.current);
    }

    if (linkImageRef.current) {
      linkImageRef.current.dataset.revealKey = 'band_link';
      observer.observe(linkImageRef.current);
    }

    return () => {
      revealObserverRef.current = null;
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (hasVideoPlayedOnce) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;

    const el = videoSectionRef.current;
    if (!el) return undefined;

    let cancelled = false;

    const tryAutoplay = async () => {
      if (cancelled) return;
      const video = videoRef.current;
      if (!video) return;
      if (hasVideoPlayedOnce) return;

      try {
        video.muted = true;
        await video.play();
        setIsVideoPlaying(true);
        setHasVideoPlayedOnce(true);
      } catch {
        // autoplay might be blocked; keep manual play button available
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        tryAutoplay();
        observer.disconnect();
      },
      { threshold: 0.6 }
    );

    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [hasVideoPlayedOnce]);

  return (
    <section className="relative overflow-hidden bg-background">
      <div
        className="absolute inset-0 opacity-70 pointer-events-none"
        style={{
          backgroundImage: patternImageUrl,
          backgroundRepeat: 'repeat',
          backgroundSize: '240px auto',
        }}
      />

      <div className="relative z-10">
        <section className="px-5 md:px-8 lg:px-10 py-10 md:py-12">
          <div className="max-w-5xl mx-auto rounded-3xl bg-white border border-border/50 shadow-elevation-2 p-6 md:p-10 backdrop-blur-sm">
            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-7 md:gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-3 rounded-2xl bg-orange-100 px-4 py-2 mb-5">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-primary-foreground">
                    <PicklingLogoMark size={20} className="text-primary-foreground" />
                  </div>
                  <p className="text-xs md:text-sm font-semibold text-orange-900">우리동네 모든 피클볼이 한 눈에!</p>
                </div>

                <h1 className="text-5xl md:text-6xl font-black tracking-tight text-[#f2683a] mb-5 leading-[0.95]">
                  Pickle-ing
                </h1>

                <p className="text-base md:text-xl font-semibold text-foreground mb-2">"피클볼로 반짝반짝 빛나는 일상"</p>
                <p className="text-sm md:text-base text-muted-foreground mb-7">
                  스피클볼..?
                  <br className="md:hidden" /> 아니 이제 <span className="font-semibold text-[#f2683a]">피클볼</span>
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={onGetStarted}
                    className="h-12 px-6 rounded-xl bg-primary text-primary-foreground font-semibold shadow-elevation-3 hover:shadow-elevation-4 transition-smooth"
                  >
                    <span className="inline-flex items-center gap-2">
                      시작하기
                      <Icon name="ArrowRightIcon" size={18} />
                    </span>
                  </button>
                </div>
              </div>

              <div className="relative rounded-2xl min-h-[250px] md:min-h-[320px] overflow-hidden">
                <img
                  ref={heroImageRef}
                  src="/assets/images/pickleball-1.png"
                  alt="커뮤니티 중심 플레이"
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out ${
                    isHeroImageVisible ? 'opacity-100 blur-0 scale-100' : 'opacity-0 blur-md scale-[1.02]'
                  }`}
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        <section style={bandStyle} className="px-5 md:px-8 lg:px-10 py-8 md:py-9">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center text-white">
            <div className="w-full justify-self-start">
              <img
                ref={liveImageRef}
                src="/assets/images/pickleball-9.png"
                alt="Pickle-ing 이미지"
                className={`w-full h-full object-cover transition-all duration-700 ease-out ${
                  visibleFeatureImages?.band_live
                    ? 'opacity-100 blur-0 scale-100'
                    : 'opacity-0 blur-md scale-[1.02]'
                }`}
                loading="lazy"
              />
            </div>
            <div className="flex flex-col gap-3 md:gap-4 justify-self-end text-left md:text-left">
              <p className="text-sm md:text-base font-semibold tracking-wide uppercase opacity-90">Live</p>
              <h2 className="text-2xl md:text-3xl font-black leading-tight">
                지금, 여기!
                <br />
                내 주변 피클볼 이벤트를
                <br />
                실시간 확인.
              </h2>
              <p className="text-xs md:text-sm opacity-90">어디서든 빠르게 매치해도, 브랜드 경험은 그대로.</p>
            </div>
          </div>
        </section>

        <section className="px-5 md:px-8 lg:px-10 py-10 md:py-12">
          <div className="max-w-5xl mx-auto grid gap-6">
            {featureRows.map((row) => {
              const isRight = row.align === 'right';
              return (
                <article
                  key={row.title}
                  className="rounded-3xl bg-white border border-border/50 p-6 md:p-8 shadow-elevation-2 backdrop-blur-sm"
                >
                  <div className={`grid md:grid-cols-2 gap-6 md:gap-10 items-center ${isRight ? 'md:[&>*:first-child]:order-2' : ''}`}>
                    <div>
                      <p className="text-xl md:text-2xl font-black text-foreground mb-1">{row.title}</p>
                      <h3 className="text-lg md:text-xl font-bold text-[#f2683a] mb-2">{row.subtitle}</h3>
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{row.description}</p>
                    </div>
                    <div
                      ref={(node) => {
                        if (!row?.title) return;
                        if (node) {
                          featureImageRefs.current[row.title] = node;
                          node.dataset.revealKey = row.title;
                          if (!visibleFeatureImages?.[row.title] && revealObserverRef.current) {
                            revealObserverRef.current.observe(node);
                          }
                        }
                      }}
                      className="min-h-[170px] rounded-2xl flex items-center justify-center"
                    >
                      <div className="w-full h-full p-4 md:p-6">
                        <div
                          className={`w-full h-full bg-white rounded-2xl flex items-center justify-center p-4 md:p-6 transition-all duration-700 ease-out ${
                            visibleFeatureImages?.[row.title]
                              ? 'opacity-100 blur-0 translate-y-0 translate-x-0'
                              : `opacity-0 blur-md translate-y-2 ${isRight ? 'translate-x-4' : '-translate-x-4'}`
                          } ${
                            row.title === 'PICK & MAKE' ? 'delay-150' : ''
                          }`}
                        >
                          <img
                            src={row.imageSrc}
                            alt={`${row.title} 일러스트`}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section style={bandStyle} className="px-5 md:px-8 lg:px-10 py-8 md:py-9">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center text-white">
            <div className="flex flex-col gap-3 md:gap-4 justify-self-start text-left">
              <p className="text-sm md:text-base font-semibold tracking-wide uppercase opacity-90">LINK</p>
              <h2 className="text-2xl md:text-3xl font-black leading-tight">
                피클볼로 연결되다.
                <br />
                이벤트를 찾고 알리는 쉽고, 강력한 도구!
                <br />
                피클볼러 들의 매력적인 커뮤니티
              </h2>
              <p className="text-xs md:text-sm opacity-90">지금 바로 들어와 매치를 확인해보세요.</p>
            </div>
            <div className="w-full justify-self-end">
              <img
                ref={linkImageRef}
                src="/assets/images/pickleball-10.png"
                alt="Pickle-ing Link 이미지"
                className={`w-full h-full max-h-[320px] md:max-h-[420px] object-cover transition-all duration-700 ease-out ${
                  visibleFeatureImages?.band_link
                    ? 'opacity-100 blur-0 scale-100'
                    : 'opacity-0 blur-md scale-[1.02]'
                }`}
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="px-5 md:px-8 lg:px-10 py-10 md:py-14 pb-16 md:pb-20">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-3xl overflow-hidden border border-border shadow-elevation-4 bg-card" ref={videoSectionRef}>
              <div className="aspect-video relative bg-black">
                <video
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full object-cover"
                  src="/assets/video/pickeball-move.mp4"
                  playsInline
                  muted
                  preload="metadata"
                  controls={false}
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onEnded={() => setIsVideoPlaying(false)}
                />

                {!isVideoPlaying && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const el = videoRef.current;
                        if (!el) return;
                        await el.play();
                        setIsVideoPlaying(true);
                      } catch {
                        // ignore
                      }
                    }}
                    className="absolute inset-0 flex items-center justify-center"
                    aria-label="영상 재생"
                  >
                    <span className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 text-sky-900 shadow-elevation-4">
                      <Icon name="PlayIcon" size={32} variant="solid" />
                    </span>
                  </button>
                )}
              </div>
            </div>

          </div>
        </section>
      </div>
    </section>
  );
};

export default HeroSection;
